import { action, observable } from "mobx";
import { IObjectMap } from "interfaces/common";
import {
  ADD_NEW_CONTRACTS_ROW,
  DELETE_CONTRACTS_EVENT,
  DELETE_CONTRACTS_FILE,
  DOWNLOAD_CONTRACTS_FILE,
  REMOVE_CONTRACTS_EDITING_KEY,
  SAVE_CONTRACTS_ENTITY,
  SET_CONTRACTS_EDITING_KEY,
  UPDATE_CONTRACTS_ENTITY,
} from "constants/events";
import ee from "../services/EventEmitter";
import AbstractLoaderStore from "./pages/AbstractLoaderStore";
import { Logistic } from "models/Logistic";
import { SelectOption } from "components/common/forms/Interfaces";
import { message } from "antd";
import { newRowIndex } from "components/tables/declarantTable/constants";
import moment from "moment/moment";
import getFileNameFromContentDisposition from "helpers/parsers";
import downloadFile from "helpers/downloadFile";
import { Pagination } from "models/Pagination";
import { Contracts } from "models/Contracts";
import { addNewContractsRow } from "components/tables/contractsTable/constants";

const contractsDateRange = {
  start_date: moment().subtract(20, "days"),
  end_date: moment().subtract(-10, "days"),
};

const defaultPagination = {
  total: 0,
  per_page: 0,
  current_page: 0,
  last_page: 999,
  from: 0,
  to: 0,
};

const defaultParameters = {
  ...contractsDateRange,
  page: 0,
};

interface DeleteRowProps {
  entityId: unknown;
  isTemporary: boolean;
}

interface UpdateRowProps {
  savingData: unknown[];
  entityId: string;
  documents: File[];
}

interface SaveUploadFileParameters {
  id: string;
  documents: File[];
}
export interface DownloadFileParameters {
  id: string;
  documents: string;
}

export type SearchInputValue = {
  value: string;
};

class ContractsStore extends AbstractLoaderStore {
  constructor() {
    super();

    this._parameters = defaultParameters;

    ee.on(DELETE_CONTRACTS_EVENT, ({ entityId, isTemporary }) => {
      this.deleteContractsRow({ entityId, isTemporary });
    });

    ee.on(SET_CONTRACTS_EDITING_KEY, (key: number) => {
      this.downloadOptions();
      this.setEditingKey(key);
    });

    ee.on(REMOVE_CONTRACTS_EDITING_KEY, (key: number) => {
      this.removeEditingKey(key);
    });

    ee.on(ADD_NEW_CONTRACTS_ROW, () => {
      this.downloadOptions();
      this.addNewRow();
    });

    ee.on(SAVE_CONTRACTS_ENTITY, ({ savingData, entityId, documents }) => {
      this.saveContractsEntity(savingData, documents);
      this.removeEditingKey(entityId);
    });

    ee.on(UPDATE_CONTRACTS_ENTITY, ({ savingData, entityId, documents }) => {
      this.updateContractsEntity({ savingData, entityId, documents });
      this.removeEditingKey(entityId);
    });

    ee.on(DOWNLOAD_CONTRACTS_FILE, ({ documents, id }) => {
      this.downloadFile({ documents, id });
    });

    ee.on(DELETE_CONTRACTS_FILE, ({ documents, id }) => {
      this.deleteFile({ documents, id });
    });
  }

  @observable
  editingKeys: number[] = [];

  @observable
  countryOptions: SelectOption[] = [];

  @observable
  statusOptions: SelectOption[] = [];

  @observable
  companyNameOptions: SearchInputValue[] = [];

  @observable
  hasMoreItems = false;

  protected _pagination: Pagination = defaultPagination;

  @observable
  isLoadingButton = false;

  @action
  load(parameters: IObjectMap | null, needUpdateData = false): void {
    if (parameters) {
      this._parameters = {
        ...this._parameters,
        ...parameters,
        page: needUpdateData ? 1 : this._pagination.current_page + 1,
      };
    }

    this.downloadSelectOptions();
    this.loadTableData(needUpdateData);
  }

  @action
  downloadSelectOptions() {
    if (this.statusOptions.length === 0) {
      this.statusSelectOptions();
    }

    if (this.countryOptions.length === 0) {
      this.countriesSelectOptions();
    }
  }

  @action
  private loadTableData(refresh = false): void {
    if (!this.hasMoreItems && !refresh) {
      return;
    }

    this.isLoadingTableData = true;

    this.hasMoreItems = false;

    this.loadContractsByClients()
      .then(async (response) => {
        const items = await response.json();

        this._data = refresh
          ? items.data
          : this._data.concat(Object.values(items.data));

        const page = items.current_page as number;
        const lastPage = items.last_page as number;

        this._pagination = {
          total: items.total,
          per_page: items.per_page,
          current_page: page,
          last_page: lastPage,
          from: items.from,
          to: items.to,
        };

        this.hasMoreItems = page < lastPage;
      })
      .catch(() => {
        this._data = [];
        this._pagination = {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          from: 0,
          to: 0,
        };
      })
      .finally(() => {
        this.isLoadingTableData = false;
      });
  }

  protected loadContractsByClients(): Promise<Response> {
    return this.apiManager.loadContractsByClients(this._parameters);
  }

  companyNameInputValues(): void {
    this.apiManager
      .getContractsSearchFieldValues("company_name")
      .then(async (response) => {
        if (response) {
          const data = await response.json();

          this.companyNameOptions = this.prepareOptionValues(data);
        }
      });
  }

  @action
  setEditingKey(key: number): void {
    this.editingKeys = [...this.editingKeys, key];
  }

  @action
  removeEditingKey(key: number): void {
    this.editingKeys = this.editingKeys.filter(
      (editingKey) => editingKey !== key
    );
  }

  getLoadResponse(parameters: IObjectMap): Promise<Response> {
    return this.apiManager.loadContractsByClients(parameters);
  }

  statusSelectOptions(): void {
    this.apiManager.getStatusOptions().then(async (response) => {
      const data = await response.json();

      this.statusOptions = this.prepareStatusOptions(data);
    });
  }

  countriesSelectOptions(): void {
    this.apiManager
      .getCountries()
      .then(async (response) => {
        this.countryOptions = Object.entries(
          (await response.json()) as Record<string, string>
        ).map<SelectOption>(([value, name]) => ({
          label: name || "",
          value: value?.toLowerCase() || 0,
        }));
      })
      .catch(() => {
        this.countryOptions = [];
      });
  }

  @action
  private addNewRow() {
    const withTemporary = (this._data as Logistic[]).some(
      (row) => row.temporary
    );

    if (!withTemporary) {
      this._data = [...this._data, addNewContractsRow];

      this.setEditingKey(newRowIndex);
    }
  }

  protected deleteContractsRow({
    entityId,
    isTemporary,
  }: DeleteRowProps): void {
    if (isTemporary) {
      this._data = (this._data as Contracts[]).filter((row) => !row?.temporary);

      return;
    }

    this.deleteContractsEntity(entityId);
  }

  protected saveContractsEntity(values: unknown[], documents: File[]): void {
    this.apiManager
      .saveContractsEntity({
        ...values,
      })
      .then(async (response) => {
        const data = await response.json();

        if (data?.entity?.id && documents) {
          const saveFilesParams = {
            documents,
            id: data?.entity?.id,
          };

          this.saveContractsDocuments(saveFilesParams);
        }

        message.success(data?.message);
      })
      .finally(() => {
        this.refresh();
      });
  }

  protected updateContractsEntity({
    savingData,
    entityId,
    documents,
  }: UpdateRowProps): void {
    this.apiManager
      .updateContractsEntity(entityId, {
        ...savingData,
      })
      .then(async (response) => {
        const data = await response.json();

        if (entityId && documents) {
          const saveFilesParams = {
            documents,
            id: entityId,
          };

          this.saveContractsDocuments(saveFilesParams);
        }
        message.success(data.message);
      })
      .finally(() => {
        this.refresh();
      });
  }

  protected deleteContractsEntity(entityId: unknown): void {
    this.apiManager
      .deleteContractsEntity(entityId)
      .then(async (response) => {
        const data = await response.json();

        message.success(data.message);
      })
      .finally(() => {
        this.refresh();
      });
  }
  @action
  saveContractsDocuments(parameters: SaveUploadFileParameters) {
    const { documents, id } = parameters;
    const formData = new FormData();

    formData.append("id", id);
    documents?.forEach((file: File) => formData.append("documents[]", file));

    this.apiManager
      .saveContractsDocuments(formData)
      .then(async (response) => {
        const data = await response.json();

        if (data?.error) {
          message.error(data.error);
        }

        if (data?.message) {
          message.success(data.message);
        }
      })
      .finally(() => {
        this.refresh();
      });
  }

  @action
  downloadFile(parameters: DownloadFileParameters) {
    this.apiManager
      .downloadContractsFile({ ...parameters })
      .then((response) => {
        if (parameters?.documents === "all") {
          const fileName = getFileNameFromContentDisposition(
            response.headers.get("Content-Disposition")
          );

          response.blob().then((blob) => {
            downloadFile(blob, fileName);
          });

          return;
        }

        response.blob().then((blob: Blob) => {
          const fileURL = window.URL.createObjectURL(blob);
          const alink = document.createElement("a");

          alink.href = fileURL;
          alink.download = parameters?.documents;
          alink.click();
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.error);
      });
  }

  @action
  deleteFile(parameters: DownloadFileParameters) {
    this.apiManager.deleteContractsFile(parameters).then(() => {
      this.refresh();
    });
  }

  prepareOptionValues(data: string[]): SearchInputValue[] {
    if (!(data.length > 0)) {
      return [];
    }

    const optionValues = data?.map((value: string) => {
      return {
        value,
      };
    });

    return [
      ...new Map(
        optionValues.map((item: SearchInputValue) => [item["value"], item])
      ).values(),
    ] as SearchInputValue[];
  }

  prepareStatusOptions(data: string[]): SelectOption[] {
    return data?.map((label: string, index) => ({
      value: index,
      label,
    }));
  }

  downloadExcel(params: IObjectMap): void {
    if (this.isLoadingButton) {
      return;
    }

    this.isLoadingButton = true;

    this.apiManager
      .downloadContractsAsExcelFile(params)
      .then((response) => {
        const fileName = getFileNameFromContentDisposition(
          response.headers.get("Content-Disposition")
        );

        response.blob().then((blob) => {
          downloadFile(blob, fileName);
        });
      })
      .catch(async (response) => {
        const data = await response.json();
        message.error(data.message);
      })
      .finally(() => {
        this.isLoadingButton = false;
      });
  }

  downloadOptions(): void {
    this.companyNameInputValues();
  }
}

export default new ContractsStore();
